import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import QRCode from 'qrcode.react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import Text from 'components/_UI/Text';
import colors from 'theme/colors';
import indexPriority from 'theme/z-index';

export default function QRCodeModal({ tickets, productName, onClose }) {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    let wakeLock = null;

    async function requestWakeLock() {
      try {
        if ('wakeLock' in navigator) {
          wakeLock = await navigator.wakeLock.request('screen');
        }
      } catch (err) {
        // Wake Lock API not supported or request failed
      }
    }

    requestWakeLock();

    return () => {
      if (wakeLock) {
        wakeLock.release().catch(() => {});
      }
    };
  }, []);

  const currentTicket = tickets[currentIndex];
  const hasMultiple = tickets.length > 1;

  const handlePrevious = () => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => Math.min(tickets.length - 1, prev + 1));
  };

  return (
    <Overlay onClick={onClose}>
      <Card onClick={(e) => e.stopPropagation()}>
        <Header>
          <CloseButton onClick={onClose}>
            <X size={24} color={colors.nightDark} />
          </CloseButton>
          <Spacer />
          <Text size="16" weight="bold" color={colors.nightDark}>
            {productName}
          </Text>
        </Header>

        <QRCodeWrapper>
          <QRCode
            value={currentTicket.code}
            size={250}
            bgColor="#ffffff"
            fgColor="#000000"
            level="Q"
          />
        </QRCodeWrapper>

        <CodeText>
          <Text size="14" color={colors.night}>
            {currentTicket.code}
          </Text>
        </CodeText>

        {hasMultiple && (
          <Pagination>
            <NavButton onClick={handlePrevious} disabled={currentIndex === 0}>
              <ChevronLeft
                size={20}
                color={currentIndex === 0 ? colors.nightLight : colors.nightDark}
              />
              <Text
                size="14"
                color={currentIndex === 0 ? colors.nightLight : colors.nightDark}
              >
                Anterior
              </Text>
            </NavButton>

            <Text size="14" color={colors.night}>
              {currentIndex + 1} de {tickets.length}
            </Text>

            <NavButton
              onClick={handleNext}
              disabled={currentIndex === tickets.length - 1}
            >
              <Text
                size="14"
                color={
                  currentIndex === tickets.length - 1
                    ? colors.nightLight
                    : colors.nightDark
                }
              >
                Proximo
              </Text>
              <ChevronRight
                size={20}
                color={
                  currentIndex === tickets.length - 1
                    ? colors.nightLight
                    : colors.nightDark
                }
              />
            </NavButton>
          </Pagination>
        )}
      </Card>
    </Overlay>
  );
}

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: ${indexPriority.modal};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Card = styled.div`
  background: ${colors.neutralLight};
  border-radius: 16px;
  max-width: 380px;
  width: 90%;
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  margin-bottom: 24px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Spacer = styled.div`
  flex: 1;
`;

const QRCodeWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: ${colors.neutralLight};
  border-radius: 8px;
`;

const CodeText = styled.div`
  margin-top: 16px;
  text-align: center;
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-top: 24px;
`;

const NavButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  padding: 8px;
`;
